import { INTEGRATIONS } from '@super/shared-types'
import { AppFolioConnector } from './AppFolioConnector'
import { BuildingLinkConnector } from './BuildingLinkConnector'
import { BuildiumConnector, buildiumCatalogClick } from './BuildiumConnector'
import { DoorLoopConnector } from './DoorLoopConnector'
import { EntrataConnector } from './EntrataConnector'
import { EpicPmConnector } from './EpicPmConnector'
import { HubspotConnector, hubspotCatalogClick } from './HubspotConnector'
import { PropertyMeldConnector } from './PropertyMeldConnector'
import { RentManagerConnector } from './RentManagerConnector'
import { RentvineConnector } from './RentvineConnector'
import { ShowMojoConnector } from './ShowMojoConnector'
import { TenantTurnerConnector } from './TenantTurnerConnector'
import { YardiBreezeConnector } from './YardiBreezeConnector'
import { YardiVoyagerConnector } from './YardiVoyagerConnector'
import type { VendorCatalogClick, VendorConnectorEntry } from './types'

/**
 * Default catalog-click for vendors whose connect flow is a form on a sub-page.
 * Navigates to the vendor's `/onboarding/integrations/:url` or
 * `/settings/integrations/:url` route where the sub-page mounts the form.
 */
function makeSubPageClick(vendorUrl: string): VendorCatalogClick {
  return ({ mode, navigate }) => {
    const base =
      mode === 'onboarding'
        ? '/onboarding/integrations'
        : '/settings/integrations'
    navigate(`${base}/${vendorUrl}`)
  }
}

/**
 * Convenience dispatch used by both catalog mounts. Enforces "look up the
 * vendor entry, invoke its catalogClick" — the only supported dispatch path.
 */
export async function dispatchCatalogClick(
  type: string,
  ctx: Parameters<VendorCatalogClick>[0],
): Promise<void> {
  const entry = VENDOR_CONNECTORS[type]
  if (!entry) return
  await entry.catalogClick(ctx)
}

/**
 * Single source of truth per integration. Both Settings' catalog and
 * Onboarding's catalog invoke `catalogClick` on card-click and mount
 * `Component` at the sub-page route. Nothing else per-vendor lives outside
 * these files.
 */
export const VENDOR_CONNECTORS: Record<string, VendorConnectorEntry> = {
  APPFOLIO: {
    Component: AppFolioConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.APPFOLIO.URL),
  },
  BUILDING_LINK: {
    Component: BuildingLinkConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.BUILDING_LINK.URL),
  },
  BUILDIUM: {
    Component: BuildiumConnector,
    catalogClick: buildiumCatalogClick,
  },
  DOORLOOP: {
    Component: DoorLoopConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.DOORLOOP.URL),
  },
  ENTRATA: {
    Component: EntrataConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.ENTRATA.URL),
  },
  EPIC_PM: {
    Component: EpicPmConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.EPIC_PM.URL),
  },
  HUBSPOT: {
    Component: HubspotConnector,
    catalogClick: hubspotCatalogClick,
  },
  PROPERTY_MELD: {
    Component: PropertyMeldConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.PROPERTY_MELD.URL),
  },
  RENT_MANAGER: {
    Component: RentManagerConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.RENT_MANAGER.URL),
  },
  RENTVINE: {
    Component: RentvineConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.RENTVINE.URL),
  },
  SHOWMOJO: {
    Component: ShowMojoConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.SHOWMOJO.URL),
  },
  TENANT_TURNER: {
    Component: TenantTurnerConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.TENANT_TURNER.URL),
  },
  YARDI_BREEZE: {
    Component: YardiBreezeConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.YARDI_BREEZE.URL),
  },
  YARDI_VOYAGER: {
    Component: YardiVoyagerConnector,
    catalogClick: makeSubPageClick(INTEGRATIONS.YARDI_VOYAGER.URL),
  },
}

export type {
  VendorCatalogClick,
  VendorConnectorEntry,
  VendorConnectorProps,
} from './types'
